import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { Loader } from '../../components';
import { getTrendsData } from '../../service/trends';

import './trends-data.css';


const DisplayTrendsData = () => {

  const [trendsData, setTrendsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAllColumns, setShowAllColumns] = useState(false);

  const handleToggleColumns = () => {
    setShowAllColumns(prevState => !prevState); // Toggle between true and false
  };

  useEffect(() => {
    downloadTrendsData(false);
  }, []);

  async function downloadTrendsData(isDownload) {
    try {
        setIsLoading(true);
      const response = await getTrendsData();
      if (response.status === 200) {
        const data = response.data.data;
        if (data.length > 0) {
          setTrendsData(data);
          setIsLoading(false);
          if (isDownload) {
            arrayToExcel(data);
          }
        }
      }
    } catch (error) {
      setIsLoading(false);
    }
  }

  function calculateOverallTrend(data) {
    const monthKeys = Object.keys(data).filter(key =>
      key.match(/(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) \d{4}$/) && !key.includes("(scan)")
    );
    monthKeys.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

    const startingMonth = monthKeys[0];
    const endingMonth = monthKeys[monthKeys.length - 2];

    const y1 = data[startingMonth];
    const y2 = data[endingMonth];

    const dateStartingMonth = new Date(startingMonth);
    const dateEndingMonth = new Date(endingMonth);

    const monthDifference = (dateEndingMonth.getFullYear() - dateStartingMonth.getFullYear()) * 12 +
      (dateEndingMonth.getMonth() - dateStartingMonth.getMonth());

    const trend = (y2 - y1) / monthDifference;
    const trendStatus = trend > 0 ? "Positive" : trend < 0 ? "Negative" : "Neutral";

    return {
      startingMonth,
      endingMonth,
      y1,
      y2,
      trend: trend.toFixed(2),
      trendStatus,
    };
  }

  function calculateTrendForLastThreeMonths(data) {
    const monthKeys = Object.keys(data).filter(key =>
      key.match(/(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) \d{4}$/)
    );

    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();

    monthKeys.sort((a, b) => {
      const dateA = new Date(a);
      const dateB = new Date(b);
      return dateB.getTime() - dateA.getTime();
    });

    const validMonths = monthKeys.filter((month) => {
      const monthDate = new Date(month);
      return !(monthDate.getMonth() === currentMonth && monthDate.getFullYear() === currentYear);
    });

    if (validMonths.length < 3) {
      console.error("Not enough data for trend calculation (requires at least 3 months).");
      return null;
    }

    const lastMonth = validMonths[0];
    const thirdLastMonth = validMonths[2];

    const lastMonthValue = data[lastMonth] || 0;
    const thirdLastMonthValue = data[thirdLastMonth] || 0;

    const dateLastMonth = new Date(lastMonth);
    const dateThirdLastMonth = new Date(thirdLastMonth);

    const xDifference = (dateLastMonth.getFullYear() - dateThirdLastMonth.getFullYear()) * 12 +
      (dateLastMonth.getMonth() - dateThirdLastMonth.getMonth());

    if (xDifference === 0) {
      console.error("Month difference is zero. Cannot calculate trend.");
      return null;
    }

    const trend = (lastMonthValue - thirdLastMonthValue) / xDifference;
    const trendStatus = trend > 0 ? "Positive" : trend < 0 ? "Negative" : "Neutral";

    return {
      lastMonth,
      thirdLastMonth,
      lastMonthValue,
      thirdLastMonthValue,
      xDifference,
      trend: trend.toFixed(2),
      trendStatus,
    };
  }

  const getTrendArrow = (trend) => {
    if (trend === "Positive") {
      return <span className="trend-arrow green-arrow">↑</span>;
    } else if (trend === "Negative") {
      return <span className="trend-arrow red-arrow">↓</span>;
    } else {
      return <span className="trend-arrow neutral-arrow">→</span>;
    }
  };

  function arrayToExcel(data) {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    const today = new Date().toISOString().slice(0, 10);
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `Trends_data_${today}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
    setIsLoading(false);
  }

  return (
    !isLoading ? (
      <div className="trends-data-wrapper">
        <div className="header-container">
          <div className="button-group">
            <h1>Trends data</h1>
            <button className='button-show' onClick={() => downloadTrendsData(true)}>
              Download Trends Data
            </button>
          </div>
          <button
            onClick={handleToggleColumns}
            className="show-more-btn"
          >
            {showAllColumns ? "← Show less" : "Show more →"}
          </button>
        </div>

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Overall Trend</th>
                <th>Overall Trend Graph</th>
                <th>Three months Trend</th>
                <th>Three months Trend Graph</th>
                {trendsData.length > 0 && Object.keys(trendsData[0]).slice(0, 8).map((key) => (
                  <th key={key}>{key}</th>
                ))}
                {showAllColumns && trendsData.length > 0 && Object.keys(trendsData[0]).slice(8).map((key) => (
                  <th key={key}>{key}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {trendsData.length > 0 && trendsData.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  <td>{calculateOverallTrend(row).trendStatus}</td>
                  <td>{getTrendArrow(calculateOverallTrend(row)?.trendStatus)}</td>
                  <td>{calculateTrendForLastThreeMonths(row)?.trendStatus}</td>
                  <td>{getTrendArrow(calculateTrendForLastThreeMonths(row)?.trendStatus)}</td>
                  {Object.values(row).slice(0, 8).map((value, colIndex) => (
                    <td key={colIndex}>{value}</td>
                  ))}
                  {showAllColumns && Object.values(row).slice(8).map((value, colIndex) => (
                    <td key={colIndex}>{value}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ) : (
      <Loader/>
    )
  );
};

export default DisplayTrendsData;
